import { DownloadOutlined, DownOutlined } from "@ant-design/icons";
import { App, Button, Dropdown, Space } from "antd";
import { useState } from "react";
import { ApiError } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { API_BASE, authFetch } from "@/api/session";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";

export type ExportColumn = {
  /** Field of a list item as the API returns it; a.b for nested. */
  key: string;
  label: string;
  kind?: "text" | "number" | "date" | "datetime";
  /** Labels for codes (statuses and the like), as the screen shows them. */
  values?: Record<string, string>;
};

type Query = Record<string, string | number | boolean | undefined>;

/**
 * Export of a list (Plan §6.15): the whole filtered list, not the page on
 * screen, as Excel or CSV, with the columns and labels the screen shows.
 * path is the list's export endpoint; query its filters (no limit/offset).
 */
export function ExportButton({ path, query, columns, filename }: { path: string; query: Query; columns: ExportColumn[]; filename: string }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [busy, setBusy] = useState(false);

  const run = async (format: "xlsx" | "csv") => {
    setBusy(true);
    const hide = message.loading(t("list.exporting"), 0);
    try {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== "" && k !== "limit" && k !== "offset") qs.set(k, String(v));
      }
      const res = await authFetch(
        new Request(`${API_BASE}${path}?${qs.toString()}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ format, filename, timezone: me?.workspace?.timezone, columns }),
        }),
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: { code?: string; message?: string; fields?: Record<string, string> } };
        throw new ApiError(res.status, body.error?.code ?? "generic", body.error?.message ?? res.statusText, body.error?.fields ?? {});
      }
      const blob = await res.blob();
      const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? `${filename}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      void message.error(errorText(err));
    } finally {
      hide();
      setBusy(false);
    }
  };

  return (
    <Dropdown
      trigger={["click"]}
      disabled={busy}
      menu={{
        items: [
          { key: "xlsx", label: t("list.exportXlsx") },
          { key: "csv", label: t("list.exportCsv") },
        ],
        onClick: ({ key }) => void run(key as "xlsx" | "csv"),
      }}
    >
      <Button icon={<DownloadOutlined />} loading={busy}>
        <Space size={4}>
          {t("list.export")}
          <DownOutlined style={{ fontSize: 10 }} />
        </Space>
      </Button>
    </Dropdown>
  );
}
